import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, ReceiptText, PlusCircle, Settings as SettingsIcon,
  Moon, Sun, LogOut, Menu, Eye, BarChart3,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ROLE_LABEL, api } from "@/lib/api";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import logo from "@/assets/logo.png";

const NAV = {
  super_admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/tenants", label: "Tenants", icon: Building2, id: "tenants" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
  ],
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/claims", label: "Claims", icon: ReceiptText, id: "claims" },
    { to: "/claims/new", label: "New Claim", icon: PlusCircle, id: "new-claim" },
    { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
  ],
  manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/claims", label: "Approvals", icon: ReceiptText, id: "claims" },
    { to: "/claims/new", label: "New Claim", icon: PlusCircle, id: "new-claim" },
    { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
  ],
  employee: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/claims", label: "My Claims", icon: ReceiptText, id: "claims" },
    { to: "/claims/new", label: "New Claim", icon: PlusCircle, id: "new-claim" },
    { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
  ],
};

export function AppLayout({ children }) {
  const { user, logout, stopImpersonation } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  const tenantId = user?.tenant?.tenant_id;
  const hasLogo = !!user?.tenant?.logo_path;
  const [tenantLogo, setTenantLogo] = useState(null);
  useEffect(() => {
    let revoked = null;
    if (tenantId && hasLogo) {
      api.get(`/tenants/${tenantId}/logo`, { responseType: "blob" })
        .then(({ data }) => { revoked = URL.createObjectURL(data); setTenantLogo(revoked); })
        .catch(() => setTenantLogo(null));
    } else {
      setTenantLogo(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [tenantId, hasLogo]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          {tenantLogo ? (
            <img src={tenantLogo} alt={user?.tenant?.name || "Logo"} className="h-8 max-w-[150px] object-contain" data-testid="sidebar-tenant-logo" />
          ) : (
            <>
              <img src={logo} alt="Claim Q" className="h-8 w-8 object-contain" />
              <span className="font-heading font-semibold tracking-tight text-lg">Claim Q</span>
            </>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.id}
              to={it.to}
              end={it.to === "/claims"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <it.icon className="h-4 w-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
              <AvatarFallback className="text-xs font-medium">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{ROLE_LABEL[user?.role]}</p>
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        {user?.impersonator && (
          <div className="bg-warning text-warning-foreground px-4 md:px-8 py-2 flex items-center justify-between gap-3 text-sm" data-testid="impersonation-banner">
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Viewing as <span className="font-semibold">{user.name}</span>
              {user.tenant?.name && <span className="opacity-80">· {user.tenant.name}</span>}
            </span>
            <Button size="sm" variant="secondary" className="h-7 rounded-sm" onClick={stopImpersonation} data-testid="exit-impersonation-btn">
              Exit
            </Button>
          </div>
        )}
        <header className="h-16 sticky top-0 z-20 bg-card/80 backdrop-blur border-b border-border flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            {user?.tenant && (
              <span className="text-sm text-muted-foreground">
                Tenant: <span className="text-foreground font-medium">{user.tenant.name}</span>
              </span>
            )}
            {user?.role === "super_admin" && (
              <span className="text-sm text-muted-foreground">Global Console</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!user?.impersonator && <ChangePasswordDialog />}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" title="Toggle theme">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {!user?.impersonator && (
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Logout">
                <LogOut className="h-5 w-5" />
              </Button>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
